import { PreferencesState } from '@abb-procure/data';
import { OverlayRef } from '@angular/cdk/overlay';
import {
  Component,
  DestroyRef,
  InjectionToken,
  computed,
  effect,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormRecord } from '@angular/forms';
import { FormRowDirective } from '../../directives/form-row.directive';
import { Button } from '../button/button.component';
import { Card } from '../card/card.component';
import { InputCheck } from '../input-check/input-check.component';

type ColumnOverlayData = {
  preference: string;
  value: string[];
  columns: Record<string, string>;
  defaults: string[];
  locked?: string[];
};

export const COLUMN_OVERLAY_CONTAINER = new InjectionToken<OverlayRef>(
  'COLUMN_OVERLAY_CONTAINER',
);
export const COLUMN_OVERLAY_DATA = new InjectionToken<ColumnOverlayData>(
  'COLUMN_OVERLAY_DATA',
);

@Component({
  templateUrl: './column-overlay.component.html',
  styleUrl: './column-overlay.component.scss',
  imports: [FormRowDirective, InputCheck, Button, Card],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ColumnOverlay {
  protected form = new FormRecord<FormControl<boolean>>({});
  protected rootClass = 'column-overlay';
  protected readonly data: ColumnOverlayData = inject(COLUMN_OVERLAY_DATA);
  protected readonly locked = this.data.locked ?? [];
  protected readonly columns = Object.keys(this.data.columns).filter(
    (column) => !this.locked.includes(column),
  );
  protected selectAllControl = new FormControl(false);

  private readonly allSelected = computed(() => {
    if (Object.values(this.selectionFormValue()).every((value) => value)) {
      return true;
    }

    if (Object.values(this.selectionFormValue()).every((value) => !value)) {
      return false;
    }

    return null;
  });

  private readonly selectionFormValue = toSignal(this.form.valueChanges, {
    initialValue: this.form.value,
  });

  private ignoreClick: boolean = true;
  private readonly container: OverlayRef = inject(COLUMN_OVERLAY_CONTAINER);
  private readonly preferencesState = inject(PreferencesState);

  constructor() {
    this.columns.forEach((column) => {
      this.form.addControl(
        column,
        new FormControl(this.data.value.includes(column), {
          nonNullable: true,
        }),
      );
    });

    if (this.columns.every((column) => this.data.value.includes(column))) {
      this.selectAllControl.setValue(true, { emitEvent: false });
    }

    this.onGlobalClick = this.onGlobalClick.bind(this);

    effect(() => {
      this.selectAllControl.setValue(this.allSelected(), { emitEvent: false });
    });

    this.selectAllControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((value) => {
        Object.keys(this.form.controls).forEach((key) => {
          this.form.controls[key].setValue(!!value);
        });
      });

    document.addEventListener('click', this.onGlobalClick);

    inject(DestroyRef).onDestroy(() => {
      document.removeEventListener('click', this.onGlobalClick);
    });
  }

  apply(): void {
    const value = this.form.getRawValue();

    const selected = Object.keys(this.data.columns).filter(
      (column) => this.locked.includes(column) || value[column],
    );

    this.preferencesState.updatePreferences({
      key: this.data.preference,
      value: JSON.stringify(selected),
    });

    this.container.dispose();
  }

  reset(): void {
    Object.keys(this.form.controls).forEach((key) => {
      this.form.controls[key].setValue(this.data.defaults.includes(key));
    });

    this.apply();
  }

  private onGlobalClick(event: MouseEvent): void {
    if (this.ignoreClick) {
      this.ignoreClick = false;
      return;
    }

    let element = event.target as HTMLElement;

    while (
      element &&
      !element.classList.contains(this.rootClass) &&
      !element.classList.contains('ng-dropdown-panel') &&
      !element.classList.contains('tag')
    ) {
      element = element.parentElement as HTMLElement;
    }

    if (element) {
      return;
    }

    this.apply();

    this.container.dispose();
    document.removeEventListener('click', this.onGlobalClick);
  }
}
